import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Rss, Flame, MessageCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { PostCard } from '../components/PostCard';
import { api } from '../api';
import { parseHero } from '../config/hero';

export function HomePage() {
  const { data: posts, isLoading } = useQuery({
    queryKey: ['posts'],
    queryFn: () => api.listPosts({}),
  });

  const { data: hotPosts } = useQuery({
    queryKey: ['hot-posts'],
    queryFn: () => api.listHotPosts(),
  });

  const { data: heroPage } = useQuery({
    queryKey: ['page', 'home-hero'],
    queryFn: () => api.getPage('home-hero'),
  });

  const hero = parseHero(heroPage?.content_md);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="mx-auto max-w-6xl px-4 pb-20 pt-24 sm:px-6 lg:px-8"
    >
      <section className="mb-16">
        <h1 className="title-reveal mb-6 font-serif text-4xl font-bold tracking-tight text-stone-900 dark:text-star sm:text-5xl">
          <span>{hero.title_pre}</span>
          <span className="glow-text">{hero.title_highlight}</span>
          <span>{hero.title_post}</span>
        </h1>
        <p className="text-lg text-stone-600 dark:text-star-dim">
          {hero.subtitle}
        </p>
      </section>

      <div className="grid gap-12 lg:grid-cols-[1fr_280px]">
        <div>
          <div className="mb-10 flex justify-end">
            <Link
              to="/rss"
              className="btn-press inline-flex items-center gap-2 rounded-full border border-stone-200 px-4 py-2 text-sm font-medium text-stone-600 transition-colors hover:border-indigo-500 hover:text-indigo-600 dark:border-indigo-500/20 dark:text-star-dim dark:hover:text-nebula-glow"
            >
              <Rss size={16} />
              RSS
            </Link>
          </div>

          {isLoading ? (
            <div className="grid gap-6 md:grid-cols-2">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-48 animate-pulse rounded-2xl bg-stone-200 dark:bg-space-800"></div>
              ))}
            </div>
          ) : (
            <div className="grid gap-6 md:grid-cols-2">
              {posts?.map((post) => (
                <PostCard key={post.id} post={post} featured={!!post.cover_url} />
              ))}
            </div>
          )}

          {!isLoading && posts?.length === 0 && (
            <div className="py-20 text-center text-stone-500 dark:text-star-dim">没有找到文章</div>
          )}
        </div>

        <aside className="hidden lg:block">
          <div className="sticky top-24 space-y-8">
            {hotPosts && hotPosts.length > 0 && (
              <section>
                <h2 className="mb-4 flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-stone-500 dark:text-star-dim">
                  <Flame size={14} className="text-orange-500" />
                  热门文章
                </h2>
                <ul className="space-y-3">
                  {hotPosts.map((post) => (
                    <li key={post.id}>
                      <Link
                        to={`/posts/${post.slug}`}
                        className="group flex items-start justify-between gap-2 text-sm"
                      >
                        <span className="min-w-0 flex-1 truncate text-stone-700 transition-colors group-hover:text-indigo-600 dark:text-star-dim dark:group-hover:text-nebula-glow">
                          {post.title}
                        </span>
                        <span className="flex shrink-0 items-center gap-1 text-xs text-stone-400 dark:text-star-dim">
                          <MessageCircle size={12} />
                          {post.comment_count}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </div>
        </aside>
      </div>
    </motion.div>
  );
}
